<template>
  <div v-if="user.isAdmin">
    <div class="flex flex-wrap flex-col md:flex-row">
      <div class="w-full md:w-1/2">
        <h1 class="mt-2 mb-4 md:mb-0 font-sans text-n-gray-dark">Admin Dashboard</h1>
      </div>
    </div>

    <div class="mt-6 mb-6">
      <h2 class="text-lg font-sans text-n-gray-dark mb-4">Active Collaborators Report</h2>
      <p class="text-sm mb-4 text-gray-600">
        Find users who hosted events or added resources within a date range.
      </p>

      <div class="flex flex-wrap items-end mb-4">
        <div class="w-full md:w-1/4 pr-0 md:pr-4 mb-3 md:mb-0">
          <label class="block text-sm font-bold mb-1">Start Date</label>
          <input type="date" v-model="startDate" class="w-full border p-2">
        </div>
        <div class="w-full md:w-1/4 pr-0 md:pr-4 mb-3 md:mb-0">
          <label class="block text-sm font-bold mb-1">End Date</label>
          <input type="date" v-model="endDate" class="w-full border p-2">
        </div>
        <div class="w-full md:w-1/4 pr-0 md:pr-4 mb-3 md:mb-0">
          <button class="button mb-0" @click="fetchReport" :disabled="loading">
            {{ loading ? 'Loading...' : 'View Report' }}
          </button>
        </div>
        <div class="w-full md:w-1/4 mb-3 md:mb-0">
          <button class="button mb-0" @click="exportCsv" :disabled="exporting || !hasResults">
            {{ exporting ? 'Exporting...' : 'Export CSV' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 mb-4">
      {{ error }}
    </div>

    <div v-if="hasResults" class="mt-4">
      <p class="text-sm mb-3 text-gray-600">
        Found <strong>{{ collaborators.length }}</strong> active collaborator(s).
      </p>
      <div class="responsive-table">
        <table class="w-full">
          <thead>
            <tr>
              <th class="text-left p-4 border border-grey-light">ID</th>
              <th class="text-left p-4 border border-grey-light">Email</th>
              <th class="text-left p-4 border border-grey-light">First Name</th>
              <th class="text-left p-4 border border-grey-light">Last Name</th>
              <th class="text-left p-4 border border-grey-light">Events</th>
              <th class="text-left p-4 border border-grey-light">Resources</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="collab in collaborators" :key="collab.id">
              <td class="p-4 border border-grey-light">{{ collab.id }}</td>
              <td class="p-4 border border-grey-light">{{ collab.email }}</td>
              <td class="p-4 border border-grey-light">{{ collab.first_name }}</td>
              <td class="p-4 border border-grey-light">{{ collab.last_name }}</td>
              <td class="p-4 border border-grey-light">{{ collab.event_titles || '-' }}</td>
              <td class="p-4 border border-grey-light">{{ collab.resource_titles || '-' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="searched && !hasResults && !loading" class="mt-4 text-center font-bold">
      No active collaborators found for the selected date range.
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'AdminDashboard',
  title: 'Admin Dashboard',
  data: () => ({
    startDate: '',
    endDate: '',
    collaborators: [],
    loading: false,
    exporting: false,
    error: '',
    searched: false
  }),
  computed: {
    ...mapGetters({
      user: 'user'
    }),
    hasResults() {
      return this.collaborators.length > 0
    }
  },
  methods: {
    validateDates() {
      if (!this.startDate || !this.endDate) {
        this.error = 'Please select both a start date and an end date.'
        return false
      }
      if (this.startDate > this.endDate) {
        this.error = 'Start date must be before end date.'
        return false
      }
      this.error = ''
      return true
    },
    fetchReport() {
      if (!this.validateDates()) return

      this.loading = true
      this.searched = true
      this.collaborators = []

      axios.get('/api/admin/active-collaborators', {
        params: {
          start_date: this.startDate,
          end_date: this.endDate
        }
      }).then((response) => {
        this.collaborators = response.data.data
      }).catch((err) => {
        this.error = 'Failed to fetch report. Please try again.'
      }).finally(() => {
        this.loading = false
      })
    },
    exportCsv() {
      if (!this.validateDates()) return

      this.exporting = true

      axios({
        url: '/api/admin/export/active-collaborators',
        method: 'GET',
        responseType: 'blob',
        params: {
          start_date: this.startDate,
          end_date: this.endDate
        }
      }).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'active-collaborators.csv')
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      }).catch(() => {
        this.error = 'Failed to export CSV. Please try again.'
      }).finally(() => {
        this.exporting = false
      })
    }
  }
}
</script>
